import { createColumnHelper } from '@tanstack/react-table';
import CommonTable from 'components/table/CommonTable';
import useTable from 'components/table/hooks/useTable';
import {
  RentalConditionObjectType,
  RentalConditions,
} from 'models/RentalCondition';
import { useTranslation } from 'react-i18next';
import { LanguageCodeUnderscored } from 'i18n/internationalization.types';
import { useParams } from 'react-router-dom';
import usePropertySettingsInitData from 'pages/property/usePropertySettingsInitData';
import { PropertyBusinessType } from 'models/Properties';
import ActionsCell from './cells/ActionsCell';
import useRentalConditionsData from './useRentalConditionsData';

const RentalConditionsTable = () => {
  const { t } = useTranslation();
  const { uid: propertyUid } = useParams();
  const { property } = usePropertySettingsInitData();

  const objectType =
    property?.businessType === PropertyBusinessType.HOTEL
      ? RentalConditionObjectType.HOTEL
      : RentalConditionObjectType.PROPERTY;

  const { isLoadingRentalConditions, rentalConditionData } =
    useRentalConditionsData({ objectType });

  const columnHelper = createColumnHelper<RentalConditions>();
  const columns = [
    columnHelper.accessor('rentalConditions', {
      id: 'rentalConditions',
      header: t(
        'pageProperty.feesTaxesAndPolicies.rentalConditions.definition',
      ),
      enableSorting: false,
      cell: (props) => {
        return props.row.original?.inheritFromAgency
          ? t(
              'pageProperty.feesTaxesAndPolicies.rentalConditions.inheritedFromAgency',
            )
          : props.cell.getValue();
      },
    }),
    // columnHelper.display({
    //   id: 'appliesTo',
    //   header: t('pageProperty.feesTaxesAndPolicies.rentalConditions.appliesTo'),
    //   cell: AppliesToCell,
    // }),
    columnHelper.display({
      id: 'actions',
      header: t('pageProperty.feesTaxesAndPolicies.rentalConditions.actions'),
      cell: ActionsCell,
      meta: {
        showCellTooltip: false,
      },
    }),
  ];

  const emptyRentalCondition = {
    objectType,
    objectUid: propertyUid as string,
    locale: LanguageCodeUnderscored.EN_US,
    inheritFromAgency: false,
    rentalConditionsUrl: '',
    rentalConditions: '',
  } satisfies RentalConditions;

  const tableInstance = useTable({
    // We always have only one rental condition
    data: rentalConditionData ? [rentalConditionData] : [emptyRentalCondition],
    columns,
    manualPagination: true,
    pageCount: 1,
  });

  return (
    <CommonTable
      tableInstance={tableInstance}
      isLoading={isLoadingRentalConditions}
      data-testid="rental-conditions-table"
    />
  );
};

export default RentalConditionsTable;
